import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from '@components/molecules/Modal';
import { Loading } from '@components/atoms/Loading';
import { ToggleSwitch } from '@components/atoms/ToggleSwitch';
import { Button } from '@components/atoms/Button';
import WorldMap, {
  WorldMapProjectMarker,
} from '@components/organisms/WorldMap';
import {
  useGeofences,
  useSetGeofenceActive,
} from '@hooks/api/geofence';
import { Geofence, GeofenceShapeType } from '@models/geofence.model';
import { Warehouse } from '@models/warehouse.model';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import PolylineIcon from '@mui/icons-material/Polyline';
import RoomIcon from '@mui/icons-material/Room';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import { cn } from '@utils/cn';
import { useTranslation } from '@hooks/useTranslation';

interface WarehouseGeofenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  warehouse: Warehouse | null;
  hideActiveToggle?: boolean;
  initialGeofenceId?: string | null;
}

const getShapeIcon = (type: GeofenceShapeType) =>
  type === GeofenceShapeType.POLYGON ? PolylineIcon : RoomIcon;

export const WarehouseGeofenceModal: React.FC<WarehouseGeofenceModalProps> = ({
  isOpen,
  onClose,
  warehouse,
  hideActiveToggle = false,
  initialGeofenceId = null,
}) => {
  const tActionMessages = useTranslation('actionMessages');
  const history = useHistory();
  const warehouseId = warehouse?.warehouseId || undefined;

  const { data: geofences = [], isLoading } = useGeofences(
    warehouseId ? { warehouseId } : undefined,
  );

  const setActiveMutation = useSetGeofenceActive();

  const [selectedGeofenceId, setSelectedGeofenceId] = useState<string | null>(
    initialGeofenceId,
  );

  useEffect(() => {
    if (!geofences.length) {
      setSelectedGeofenceId(null);
      return;
    }
    setSelectedGeofenceId((prev) => {
      if (initialGeofenceId && geofences.some((g) => g.geofenceId === initialGeofenceId)) {
        return initialGeofenceId;
      }
      if (prev && geofences.some((g) => g.geofenceId === prev)) return prev;
      return geofences[0].geofenceId;
    });
  }, [geofences, initialGeofenceId]);

  const selectedGeofence: Geofence | null = useMemo(
    () =>
      geofences.find((g) => g.geofenceId === selectedGeofenceId) ??
      geofences[0] ??
      null,
    [geofences, selectedGeofenceId],
  );

  // The WorldMap renders projects as markers — we reuse it for warehouses by
  // packaging the warehouse as a single "marker" item so the location is shown.
  const warehouseAsMarker: WorldMapProjectMarker[] = useMemo(() => {
    if (!warehouse?.warehouseId || !warehouse.lat || !warehouse.long) return [];
    return [
      {
        projectId: warehouse.warehouseId,
        name: warehouse.name || 'Warehouse',
        lat: warehouse.lat as number,
        lng: warehouse.long as number,
      },
    ];
  }, [warehouse]);

  const projectIdsWithGeofence = useMemo(() => {
    const set = new Set<string>();
    if (
      warehouse?.warehouseId &&
      selectedGeofence?.shape.type === GeofenceShapeType.POLYGON
    ) {
      set.add(warehouse.warehouseId);
    }
    return set;
  }, [selectedGeofence, warehouse]);

  const handleToggleActive = useCallback(
    async (next: boolean) => {
      if (!selectedGeofence) return;
      try {
        await setActiveMutation.mutateAsync({
          geofenceId: selectedGeofence.geofenceId,
          isActive: next,
        });
        toast.success(next ? tActionMessages.geofenceEnabled : tActionMessages.geofenceDisabled);
      } catch (err: any) {
        const message =
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          tActionMessages.failedToUpdateGeofence;
        toast.error(message);
      }
    },
    [
      selectedGeofence,
      setActiveMutation,
      tActionMessages.geofenceEnabled,
      tActionMessages.geofenceDisabled,
      tActionMessages.failedToUpdateGeofence,
    ],
  );

  const hasCoordinates = !!(warehouse?.lat && warehouse?.long);
  const hasGeofences = geofences.length > 0;
  const SelectedShapeIcon = selectedGeofence
    ? getShapeIcon(selectedGeofence.shape.type)
    : RoomIcon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Warehouse Geofence"
      size="large"
      cancelText="Close"
    >
      <div className="flex flex-col gap-4">
        {/* Selected geofence header */}
        {selectedGeofence && (
          <div className="bg-white rounded-lg ring-1 ring-gray-900/5 shadow-sm p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: `${selectedGeofence.category?.color || selectedGeofence.color || '#3B82F6'}20`,
                    color:
                      selectedGeofence.category?.color ||
                      selectedGeofence.color ||
                      '#3B82F6',
                  }}
                >
                  <SelectedShapeIcon sx={{ fontSize: 22 }} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {selectedGeofence.name}
                  </p>
                  {warehouse?.address && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {warehouse.address}
                    </p>
                  )}
                </div>
              </div>

              {!hideActiveToggle && (
                <ToggleSwitch
                  leftLabel="Disabled"
                  rightLabel="Active"
                  checked={selectedGeofence.isActive}
                  onChange={handleToggleActive}
                  disabled={setActiveMutation.isPending}
                />
              )}
            </div>

            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
              {selectedGeofence.category?.name && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: `${selectedGeofence.category.color || '#3B82F6'}20`,
                    color: selectedGeofence.category.color || '#3B82F6',
                  }}
                >
                  {selectedGeofence.category.name}
                </span>
              )}
              <span className="text-xs text-gray-500">
                {selectedGeofence.shape.type === GeofenceShapeType.POLYGON
                  ? 'Polygon'
                  : `Circle${selectedGeofence.shape.radiusMeters ? ` · ${selectedGeofence.shape.radiusMeters}m` : ''}`}
              </span>
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-3 h-[420px]">
          {/* Left: geofence list */}
          <div className="md:w-64 w-full md:h-full h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loading />
              </div>
            ) : hasGeofences ? (
              <ul className="divide-y divide-gray-100">
                {geofences.map((g) => {
                  const ShapeIcon = getShapeIcon(g.shape.type);
                  const tint = g.category?.color || g.color || '#3B82F6';
                  const isActive = g.geofenceId === selectedGeofence?.geofenceId;
                  return (
                    <li key={g.geofenceId}>
                      <button
                        type="button"
                        onClick={() => setSelectedGeofenceId(g.geofenceId)}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2 text-left transition-colors',
                          isActive
                            ? 'bg-indigo-50 ring-1 ring-inset ring-indigo-200'
                            : 'hover:bg-gray-50',
                        )}
                      >
                        <span
                          className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                          style={{
                            backgroundColor: `${tint}20`,
                            color: tint,
                          }}
                        >
                          <ShapeIcon sx={{ fontSize: 18 }} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-gray-900 truncate">
                            {g.name}
                          </span>
                          <span className="block text-xs text-gray-500 truncate">
                            {g.shape.type === GeofenceShapeType.POLYGON
                              ? 'Polygon'
                              : `Circle${g.shape.radiusMeters ? ` · ${g.shape.radiusMeters}m` : ''}`}
                          </span>
                        </span>
                        {!g.isActive && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                            Off
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="flex items-center justify-center h-full text-xs text-gray-500 px-4 text-center">
                No geofences for this warehouse.
              </div>
            )}
          </div>

          {/* Right: map */}
          <div className="flex-1 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 relative min-h-[240px]">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/40">
                <Loading />
              </div>
            )}
            {hasCoordinates ? (
              <WorldMap
                projects={warehouseAsMarker}
                geofences={selectedGeofence ? [selectedGeofence] : []}
                projectIdsWithGeofence={projectIdsWithGeofence}
                showProjects
                showGeofences
                fitPadding={30}
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center text-sm text-gray-500 px-6">
                <InfoOutlinedIcon
                  className="text-gray-300 mb-2"
                  sx={{ fontSize: 40 }}
                />
                <p>This warehouse has no coordinates configured.</p>
                <p className="text-xs text-gray-400 mt-1">
                  Edit the warehouse to set its location before adding a geofence.
                </p>
              </div>
            )}
          </div>
        </div>

        {selectedGeofence ? (
          <Button
            variant="secondary"
            leftIcon={<LaunchIcon sx={{ fontSize: 16 }} />}
            onClick={() => {
              onClose();
              history.push(`/geofences?selected=${selectedGeofence.geofenceId}`);
            }}
          >
            Go to geofence
          </Button>
        ) : hasCoordinates && !isLoading ? (
          <div className="bg-amber-50 rounded-lg ring-1 ring-amber-200 p-4 flex items-start gap-3">
            <InfoOutlinedIcon
              className="text-amber-500 flex-shrink-0 mt-0.5"
              sx={{ fontSize: 20 }}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900 truncate">
                {warehouse?.name || 'No geofence configured'}
              </p>
              {warehouse?.address && (
                <p className="text-xs text-amber-800/80 mt-0.5 truncate">
                  {warehouse.address}
                </p>
              )}
              <p className="text-xs text-amber-800 mt-2">
                The marker on the map shows the warehouse location. Create a
                geofence to track who is inside.
              </p>
              <Link
                to="/geofences"
                className="inline-block mt-2 text-xs font-medium text-indigo-600 hover:text-indigo-700"
              >
                Create geofence →
              </Link>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default WarehouseGeofenceModal;
